import { isTradeableSecType } from "../utils/isTradeableSecType";
import { isValidExchange } from "../utils/isValidExchange";
import configService from "./configService";
import httpService from "./httpService";

/**
 * Reference-data figures are Prisma Decimal server-side and can serialize as
 * strings (see the Contract model in types/contract.ts). Normalizing here keeps
 * the declared `number | null` on our types honest and, critically, keeps a
 * decimal zero recognisable as zero -- `"0.00" === 0` is false, so an
 * unnormalized string zero would slip past the liquidity gate's missing-value
 * check and be compared as a real measurement.
 *
 * Anything absent, blank or non-numeric collapses to null, which the gate
 * treats identically to zero.
 */
const toNumberOrNull = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

// Stock search API service - New unified contracts/find endpoint
export interface StockSearchResult {
  conId: number;
  symbol: string;
  secType: string;
  exchange: string;
  primaryExchange: string;
  currency: string;
  isin: string;
  longName: string;
  optionAllowed: boolean;
  optionExchange: string | null;
  optionType: string;
  /**
   * Average daily traded value in USD. Null when never computed. Can arrive as
   * a string -- normalize with toNumberOrNull before comparing.
   */
  avgDollarVolume: number | string | null;
  /** Market cap converted to USD. Null when never computed. May be a string. */
  marketCapUsd: number | string | null;
}

export interface StockSearchResponse {
  message: string;
  contracts: StockSearchResult[];
  count: number;
  searchParams: {
    optionsAllowed: boolean;
    limit: number;
    searchString: string;
  };
}

export interface SearchResult {
  ticker: string;
  name: string;
  isin: string;
  sector: string;
  marketCap: number;
  matchType: "ticker" | "name" | "isin";
  conId?: number;
  primaryExchange?: string;
  currency?: string;
  secType?: string;
  // Additional instrument metadata used by baskets/portfolios
  exchange?: string;
  longName?: string;
  optionAllowed?: boolean;
  optionExchange?: string | null;
  optionType?: string;
  // Liquidity gate inputs (PWA-2597). Null when never computed server-side.
  // Distinct from `marketCap` above, which is not populated by this API.
  avgDollarVolume?: number | null;
  marketCapUsd?: number | null;
}

export interface PriceResult {
  price: number;
  currency: string;
  source: string;
  timestamp: string;
}

class StockSearchService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = configService.getApiBaseUrl();
  }

  async searchStocks(
    ticker: string,
    _fuzzy: boolean = false,
    limit: number = 20,
    signal?: AbortSignal
  ): Promise<SearchResult[]> {
    try {
      const url = `/contracts/find?search_string=${encodeURIComponent(ticker)}&limit=${limit}`;
      console.log("StockSearchService: Making request to:", url);

      const response = await httpService.get<{
        message: string;
        contracts: Array<{
          conId: number;
          symbol: string;
          secType: string;
          exchange: string;
          primaryExchange: string;
          currency: string;
          isin: string;
          longName: string;
          optionAllowed: boolean;
          optionExchange: string | null;
          optionType: string;
          // Wire type, before normalization -- Prisma Decimal can arrive as a string.
          avgDollarVolume: number | string | null;
          marketCapUsd: number | string | null;
        }>;
        count: number;
      }>(url, { signal });
      const data = response.data;
      console.log("StockSearchService: Response data:", data);

      // Transform the API response to match the expected SearchResult format
      const transformedResults = data.contracts.map(contract => ({
        ticker: contract.symbol,
        name: contract.longName || contract.symbol,
        isin: contract.isin || "",
        sector: "", // Not available from API
        marketCap: 0, // Not available from API
        matchType: "ticker" as const,
        conId: contract.conId,
        primaryExchange: contract.primaryExchange,
        currency: contract.currency,
        secType: contract.secType,
        exchange: contract.exchange,
        longName: contract.longName,
        optionAllowed: contract.optionAllowed,
        optionExchange: contract.optionExchange,
        optionType: contract.optionType,
        avgDollarVolume: toNumberOrNull(contract.avgDollarVolume),
        marketCapUsd: toNumberOrNull(contract.marketCapUsd),
      }));

      // Filter out non-tradeable contracts
      const filteredResults = transformedResults
        .filter(isValidExchange)
        .filter(isTradeableSecType);

      console.log("StockSearchService: Transformed results with secType:", filteredResults);
      return filteredResults;
    } catch (error) {
      console.error("Stock search error:", error);
      throw error;
    }
  }

  async getContracts(conIdList: number[]): Promise<SearchResult[]> {
    try {
      const response = await httpService.post<{
        contracts: Array<{
          conId: number;
          symbol: string;
          secType: string;
          exchange: string;
          primaryExchange: string;
          currency: string;
          isin: string;
          longName: string;
          optionAllowed: boolean;
          optionExchange: string | null;
          optionType: string;
        }>;
      }>("/contracts/list", { conIdList });

      // Transform to SearchResult with full metadata
      const transformedContracts = response.data.contracts.map(contract => ({
        ticker: contract.symbol,
        name: contract.longName || contract.symbol,
        isin: contract.isin || "",
        sector: "",
        marketCap: 0,
        matchType: "ticker" as const,
        conId: contract.conId,
        primaryExchange: contract.primaryExchange,
        currency: contract.currency,
        secType: contract.secType,
        exchange: contract.exchange,
        longName: contract.longName,
        optionAllowed: contract.optionAllowed,
        optionExchange: contract.optionExchange,
        optionType: contract.optionType,
      }));

      return transformedContracts;
    } catch (error) {
      console.error("Contracts list fetch error:", error);
      throw error;
    }
  }
}

export const stockSearchService = new StockSearchService();
